export type GpxEditSubMode = 'geometry' | 'extend' | 'trim' | 'waypoints';

export interface IGpxEditToolbarCallbacks {
    setSubMode(mode: GpxEditSubMode | null): void;
    openMetadata(): void;
    /** One-shot action, not a sub-mode: flips the route's direction and returns. */
    reverse(): void;
    undo(): void;
    redo(): void;
    discard(): void;
    done(): void;
}

/**
 * Binds the vertical editing toolbar (#gpxEditToolbar): sub-mode buttons,
 * undo/redo state and the dirty indicator.
 */
export class GpxEditToolbar {

    public constructor(
        private callbacks: IGpxEditToolbarCallbacks
    ) {
        const self = this;

        // The toolbar sits INSIDE #map so it survives OpenLayers' full-screen mode,
        // which means every click on it also reaches the map. While a Draw is running
        // that click places a point — so pressing Undo removed the last point and
        // immediately added a new one, and nothing appeared to happen. OL works off
        // the pointer events, so stopping those at the toolbar is what matters; the
        // buttons' own handlers have already run by the time this fires.
        const toolbar = document.getElementById('gpxEditToolbar');
        ['pointerdown', 'pointerup', 'click', 'dblclick'].forEach(type => {
            toolbar.addEventListener(type, (event: Event) => event.stopPropagation());
        });

        document.querySelectorAll('#gpxEditToolbar [data-gpx-submode]').forEach(button => {
            button.addEventListener('click', () => {
                self.callbacks.setSubMode(<GpxEditSubMode>button.getAttribute('data-gpx-submode'));
            });
        });

        document.getElementById('gpxEditMetadata').addEventListener('click', () => self.callbacks.openMetadata());
        document.getElementById('gpxEditReverse').addEventListener('click', () => self.callbacks.reverse());
        document.getElementById('gpxEditUndo').addEventListener('click', () => self.callbacks.undo());
        document.getElementById('gpxEditRedo').addEventListener('click', () => self.callbacks.redo());
        document.getElementById('gpxEditDiscard').addEventListener('click', () => self.callbacks.discard());
        document.getElementById('gpxEditDone').addEventListener('click', () => self.callbacks.done());
    }

    public show = (): void => {
        document.getElementById('gpxEditToolbar').classList.remove('d-none');
    }

    public hide = (): void => {
        document.getElementById('gpxEditToolbar').classList.add('d-none');
    }

    public setActiveSubMode = (mode: GpxEditSubMode | null): void => {
        document.querySelectorAll('#gpxEditToolbar [data-gpx-submode]').forEach(button => {
            button.classList.toggle('active', button.getAttribute('data-gpx-submode') === mode);
        });
    }

    public setUndoRedoState = (canUndo: boolean, canRedo: boolean, isDirty: boolean): void => {
        (<HTMLButtonElement>document.getElementById('gpxEditUndo')).disabled = !canUndo;
        (<HTMLButtonElement>document.getElementById('gpxEditRedo')).disabled = !canRedo;
        document.getElementById('gpxEditDirtyDot').classList.toggle('d-none', !isDirty);
    }
}
